// Scrivener import in the browser (`ScrivenerImporter`, SuiteImport, ported step for step; see
// `design-docs/Scrivener import in the browser — design.md`). A package becomes a new project, or re-imports into the
// project it was imported into before, three-way: every binder item is a fragment keyed by its Scrivener UUID
// (`source` `scrivener:<uuid>`), and what Scrivener said at the last import is kept on the fragment (`importBase`), so
// only what changed in Scrivener since is taken, the writer's own edits stay, and where both changed one thing the
// suite's stays with Scrivener's text offered as a suggestion. Fields merge the same way against `settings.scrivenerFields`
// (`fieldMerge.ts`), tags as a set, the binder's structure by its import signature (`settings.importBases`). Snapshots
// are back-dated history on new fragments; Scrivener's media files and inline images become attachments whose bytes the
// app uploads. Ids, keys and JSON forms are the Mac's, so either app can re-import over the other's import.
//
// `ScrivenerImport` runs in steps (`prepare`, one `importItem` per binder item, `finish`), so the app can show progress
// and yield between items, or run it whole inside one synchronous edit of an open project (`importScrivenerPackageSync`).
import * as A from '@automerge/automerge';
import type { FragmentKind, InkColor, MetadataValue } from '../contract.ts';
import { sequence } from '../automerge/fractionalIndex.ts';
import { sha256Hex } from '../hashing.ts';
import { readFragment, readProject, type Arrangement, type ArrangementNode, type ProjectRecord } from '../model/documents.ts';
import { tree, type ArrangementTreeNode } from '../model/arrangement.ts';
import { isLinkAddress, linkAddress, linking, plainText, removingLinks, wordCount } from '../proseMarkdown.ts';
import { lines as fountainLines, plainText as fountainPlainText } from '../fountain.ts';
import { isActOrSceneHeading, scriptFormatNamed, scrivenerScriptFountain } from './scrivenerScript.ts';
import { isStageFormat, type ScriptFormat } from '../scriptFormat.ts';
import { nearest } from '../quoteSearch.ts';
import { addingTag, fragmentTags, hasTag, joinTags, parseTags, removingTag, TAGS_FIELD } from '../tags.ts';
import { fragmentDocName, metadataScalar, putNode, type EditOptions } from '../edit/editProject.ts';
import { StoreEditor, type FieldSpec } from '../edit/storeEditor.ts';
import { ReviseEditor } from '../revise/edits.ts';
import { categoryNamed, readCommentCategories } from '../revise/commentCategories.ts';
import { inkCategoryName, scrivenerCommentInk } from './commentColors.ts';
import { bool, str, timestamp, type Writer } from '../edit/typed.ts';
import { toISO8601 } from '../review/json.ts';
import { convertRTF, rtfPlainText, type RTFConversion } from './rtf.ts';
import { encodeImportBase, IMPORT_BASE_KEY, readImportBase, sameValue, type ImportBase } from './importBase.ts';
import { decodeFieldBase, encodeFieldBase, mergeField, SCRIVENER_FIELDS_KEY } from './fieldMerge.ts';
import { isRootFolder, ScrivenerError, type BinderItem, type ScrivenerPackage } from './scrivenerPackage.ts';
import { parseXML } from './xmlTree.ts';
import { historySummary, recordImportedHistory, type HistorySummary } from '../importedHistory.ts';

export const SCRIVENER_SOURCE = 'scrivener';

export interface ImportIssue { item: string; message: string }

/** `ImportReport`. */
export interface ScrivenerImportReport {
  projectID: string;
  projectTitle: string;
  isReimport: boolean;
  fragmentsCreated: number;
  fragmentsUpdated: number;
  fragmentsUnchanged: number;
  fragmentsRemoved: number;
  snapshotsImported: number;
  commentsImported: number;
  linksPreserved: number;
  highlightsPreserved: number;
  /** Fragments changed in both Scrivener and the suite since the last import (suite kept). */
  conflicts: number;
  /** Arrangements whose Scrivener structure went to a new alternate because both sides changed it. */
  structureConflicts: string[];
  attachmentsStored: number;
  /** Scrivener's writing history, when it kept one. */
  writingHistory?: HistorySummary;
  arrangements: string[];
  totalWords: number;
  issues: ImportIssue[];
  verification: { itemsChecked: number; textMismatches: string[]; structureMismatches: string[] };
}

export interface ScrivenerImportOptions {
  /** The writer's name, for comments without an author and the checkpoints. */
  author: string;
  device?: string;
  locale?: string;
  now?: Date;
  /** An existing project's documents: the package re-imports into it (its `source` must be this package's). */
  docs?: Map<string, A.Doc<unknown>>;
  /** Imports even a package that looks like an older copy of the project (`olderCopyReason`). */
  allowOlder?: boolean;
  /** Pins ids and actors (tests). */
  makeID?: () => string;
  newActor?: () => string;
  actor?: string;
  /** Called before each item, for a progress line; `importScrivenerPackage` yields to the event loop after each. */
  progress?: (done: number, total: number, title: string) => void;
  yield?: () => Promise<void>;
}

/** A file Scrivener kept that the app stores as an attachment (uploaded by the app; the fragment already names it). */
export interface ImportedAttachment { sha256: string; filename: string; mimeType: string; bytes: Uint8Array }

export interface ScrivenerImportResult {
  projectID: string;
  type: 'manuscript' | 'screenplay';
  docs: Map<string, A.Doc<unknown>>;
  report: ScrivenerImportReport;
  /** New attachments, by SHA-256. */
  attachments: ImportedAttachment[];
}

const labelOption = (id: string) => `label-${id}`;
const statusOption = (id: string) => `status-${id}`;
const sectionOption = (id: string) => `section-${id.toLowerCase()}`;
const customField = (id: string) => `scrivener-${id}`;
export const scrivenerNodeID = (uuid: string) => `scrivener-${uuid.toLowerCase()}`;
const IMPORT_MESSAGES = new Set(['Imported from Scrivener', 'Re-imported from Scrivener']);
const OBJECT_REPLACEMENT = String.fromCharCode(0xfffc);

type ChangeFn = Parameters<StoreEditor['change']>[3];

/** An editor whose clock the importer sets, so each change carries Scrivener's dates as the Mac's do. */
export class ImportEditor extends ReviseEditor {
  clock = new Date();

  constructor(docs: Map<string, A.Doc<unknown>>, options: EditOptions) {
    super(docs, { ...options, now: () => this.clock });
  }

  at<T>(date: Date, fn: () => T): T {
    const was = this.clock;
    this.clock = date;
    try { return fn(); } finally { this.clock = was; }
  }

  edit(name: string, message: string | undefined, at: Date, fn: ChangeFn): boolean {
    return this.change(name, message, at, fn);
  }

  document(name: string) { return this.doc(name); }
  has(name: string) { return this.docs.has(name); }
  newID() { return this.id(); }
}

/** Scrivener's field definitions as the project stores them (`ScrivenerImporter.fields(of:)`). */
export function scrivenerFields(pkg: ScrivenerPackage): FieldSpec[] {
  const fields: FieldSpec[] = [
    { id: 'label', name: 'Label', type: 'enum', options: pkg.labels.map((l) => ({ id: labelOption(l.id), name: l.name, ...(l.color ? { color: l.color } : {}) })), order: 1 },
    { id: 'status', name: 'Status', type: 'enum', options: pkg.statuses.map((s) => ({ id: statusOption(s.id), name: s.name })), order: 0 },
  ];
  if (pkg.sectionTypes.length) {
    fields.push({ id: 'section-type', name: 'Section type', type: 'enum', options: pkg.sectionTypes.map((s) => ({ id: sectionOption(s.id), name: s.name })), order: 4 });
  }
  pkg.customFields.forEach((f, n) => {
    const type = f.type === 'Checkbox' ? 'boolean' : f.type === 'List' ? 'enum' : f.type === 'Date' ? 'date' : 'text';
    fields.push({ id: customField(f.id), name: f.title, type, options: f.options.map((o) => ({ id: `${customField(f.id)}-${o.id}`, name: o.name })), order: 10 + n });
  });
  return fields;
}

/** `ScrivenerImporter.scriptFormat`: the format the project's scripts were written in, by its script format's name. */
export function scrivenerScriptFormat(pkg: ScrivenerPackage): ScriptFormat { return scriptFormatNamed(pkg.scriptFormatName); }

/** Whether most text documents in the Draft are in script mode. */
export function isScreenplay(pkg: ScrivenerPackage): boolean {
  const draft = pkg.binder.find((b) => b.type === 'DraftFolder');
  if (!draft) return false;
  const texts = pkg.descendants(draft).filter((i) => i.type === 'Text');
  return texts.length > 0 && texts.filter((t) => t.isScript).length * 2 >= texts.length;
}

const shortDate = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

/** Why `pkg` looks like an older copy of the project (`ScrivenerImporter.olderCopyReason`), or null. */
export function olderCopyReason(pkg: ScrivenerPackage, project: ProjectRecord): string | null {
  const recorded = typeof project.settings.scrivenerLastEdit === 'string' ? new Date(project.settings.scrivenerLastEdit) : null;
  const edited = pkg.lastModified;
  if (recorded && !Number.isNaN(recorded.getTime()) && edited && edited.getTime() < recorded.getTime() - 60_000) {
    return `it was last edited ${shortDate(edited)}; the copy imported before was edited ${shortDate(recorded)}`;
  }
  const imported = [...project.fragments.values()].filter((e) => e.source?.startsWith(`${SCRIVENER_SOURCE}:`) && !e.trashed);
  const present = new Set(pkg.allItems().map((i) => i.uuid));
  const missing = imported.filter((e) => !present.has(e.source!.slice(SCRIVENER_SOURCE.length + 1))).length;
  if (imported.length >= 10 && missing * 5 > imported.length) {
    return `it lacks ${missing} of the project's ${imported.length} documents, which would move to the Trash`;
  }
  return null;
}

const mimeTypes: Record<string, string> = {
  pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', tif: 'image/tiff', tiff: 'image/tiff',
  heic: 'image/heic', webp: 'image/webp', webarchive: 'application/x-webarchive', mp3: 'audio/mpeg', m4a: 'audio/mp4', wav: 'audio/wav',
  mp4: 'video/mp4', mov: 'video/quicktime', txt: 'text/plain', md: 'text/markdown', html: 'text/html', rtf: 'text/rtf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};
export const mimeType = (filename: string) => mimeTypes[filename.split('.').pop()?.toLowerCase() ?? ''] ?? 'application/octet-stream';

interface AttachmentRef { sha256: string; filename: string; mimeType: string }

export class ScrivenerImport {
  readonly report: ScrivenerImportReport;
  readonly items: BinderItem[];
  readonly isReimport: boolean;
  readonly type: 'manuscript' | 'screenplay';
  readonly projectID: string;
  private readonly editor: ImportEditor;
  private readonly docs: Map<string, A.Doc<unknown>>;
  private readonly now: Date;
  private readonly inDraft: Set<string>;
  private readonly trashUUIDs: Set<string>;
  private readonly fragmentByUUID = new Map<string, string>();
  /** Every item's fragment id before any text goes in, so a link to a document further on can name it. */
  private readonly linkTargets = new Map<string, string>();
  private readonly conversions = new Map<string, RTFConversion>();
  private readonly verifiable = new Set<string>();
  private readonly seen = new Set<string>();
  private readonly attachments = new Map<string, ImportedAttachment>();
  private readonly draft: BinderItem | undefined;
  private readonly trashRoot: BinderItem | undefined;

  constructor(readonly pkg: ScrivenerPackage, readonly options: ScrivenerImportOptions) {
    this.now = options.now ?? new Date();
    const source = `${SCRIVENER_SOURCE}:${pkg.identifier}`;
    let project: ProjectRecord | null = null;
    if (options.docs) {
      const doc = options.docs.get('project');
      if (!doc) throw new ScrivenerError('notAScrivenerProject', 'The open project has no project document.');
      project = readProject(doc);
      if (project.source !== source) {
        throw new ScrivenerError('differentProject', `“${pkg.title}” isn’t the Scrivener project “${project.title}” was imported from. Import it as a new project instead.`, project.title);
      }
      if (!options.allowOlder) {
        const reason = olderCopyReason(pkg, project);
        if (reason) throw new ScrivenerError('olderCopy', `This looks like an older copy of “${project.title}”: ${reason}. It wasn’t imported.`, project.title);
      }
    }
    this.isReimport = project !== null;
    this.report = {
      projectID: '', projectTitle: pkg.title, isReimport: this.isReimport, fragmentsCreated: 0, fragmentsUpdated: 0, fragmentsUnchanged: 0, fragmentsRemoved: 0,
      snapshotsImported: 0, commentsImported: 0, linksPreserved: 0, highlightsPreserved: 0, conflicts: 0, structureConflicts: [], attachmentsStored: 0,
      arrangements: [], totalWords: 0, issues: [], verification: { itemsChecked: 0, textMismatches: [], structureMismatches: [] },
    };
    if (project) {
      this.type = project.settings.projectType === 'screenplay' ? 'screenplay' : 'manuscript';
      this.docs = options.docs!;
    } else {
      const created = pkg.binder.map((b) => b.created).filter((d): d is Date => d !== null).sort((a, b) => a.getTime() - b.getTime())[0] ?? this.now;
      this.type = isScreenplay(pkg) ? 'screenplay' : 'manuscript';
      // A script's format comes from Scrivener's: a stage play is laid out as one.
      const scriptFormat = this.type === 'screenplay' ? scrivenerScriptFormat(pkg) : 'screen';
      const doc = StoreEditor.createProject({ title: pkg.title, type: this.type, scriptFormat, createdAt: created, locale: options.locale, id: options.makeID?.(), arrangementID: 'manuscript', actor: options.actor });
      this.docs = new Map([['project', doc]]);
    }
    this.editor = new ImportEditor(this.docs, { device: options.device ?? 'web', author: options.author, makeID: options.makeID, newActor: options.newActor, actor: options.actor });
    this.projectID = this.project().id;
    this.report.projectID = this.projectID;
    this.draft = pkg.binder.find((b) => b.type === 'DraftFolder');
    this.trashRoot = pkg.binder.find((b) => b.type === 'TrashFolder');
    this.inDraft = new Set(this.draft ? pkg.descendants(this.draft).map((i) => i.uuid) : []);
    this.trashUUIDs = new Set(this.trashRoot ? pkg.descendants(this.trashRoot).map((i) => i.uuid) : []);
    this.items = pkg.allItems().filter((i) => !isRootFolder(i));
  }

  private project(): ProjectRecord { return readProject(this.editor.document('project')); }
  private issue(item: string, message: string) { this.report.issues.push({ item, message }); }

  /** The project's source, the "Before import" checkpoint of a re-import, and the fields. */
  prepare(): void {
    const { editor, pkg, now } = this;
    if (this.isReimport) {
      editor.at(now, () => editor.createCheckpointBy('Before import', this.options.author, 'before-import'));
    } else {
      const created = this.project().createdAt;
      editor.edit('project', undefined, created, (w) => w.put([], 'source', str(`${SCRIVENER_SOURCE}:${pkg.identifier}`)));
    }
    for (const [id, entry] of this.project().fragments) {
      if (entry.source?.startsWith(`${SCRIVENER_SOURCE}:`)) this.fragmentByUUID.set(entry.source.slice(SCRIVENER_SOURCE.length + 1), id);
    }
    this.defineFields();
    for (const [uuid, id] of this.fragmentByUUID) this.linkTargets.set(uuid, id);
    for (const item of this.items) if (!this.linkTargets.has(item.uuid)) this.linkTargets.set(item.uuid, editor.newID());
  }

  /** `defineFields`: Scrivener's labels, statuses, section types and custom fields, merged three-way with the project's. */
  private defineFields(): void {
    const { editor, pkg } = this;
    const stamp = this.isReimport ? this.now : this.project().createdAt;
    editor.at(stamp, () => {
      if (Object.keys(pkg.keywordTitles).length) editor.ensureTagsField(undefined);
      const theirs = scrivenerFields(pkg);
      const base = decodeFieldBase(this.project().settings[SCRIVENER_FIELDS_KEY]);
      const baseByID = base ? new Map(base.map((f) => [f.id, f])) : null;
      for (const field of theirs) {
        const ours = this.project().fields.find((f) => f.id === field.id);
        mergeField(editor, () => this.project().fields, field, baseByID?.get(field.id) ?? ours, ours, (message) => this.issue(field.name, message));
      }
      editor.edit('project', undefined, stamp, (w) => w.putIfChanged(['settings'], SCRIVENER_FIELDS_KEY, str(encodeFieldBase(theirs))));
    });
  }

  private kindOf(item: BinderItem): FragmentKind {
    switch (item.type) {
      case 'Text': case 'Folder': return this.inDraft.has(item.uuid) ? (item.isScript && this.type === 'screenplay' ? 'screenplay' : 'prose') : 'note';
      case 'PDF': case 'Image': case 'WebArchive': case 'Media': case 'Audio': case 'Video': return 'research';
      default: return 'note';
    }
  }

  /** `importItem`: one binder item into its fragment, new or three-way. */
  importItem(index: number): void {
    const item = this.items[index];
    const { editor, pkg, now } = this;
    const kind = this.kindOf(item);
    const created = item.created ?? now;
    const snapshots = pkg.snapshots(item.uuid);
    // Scrivener can take a snapshot after the item's recorded modification date; keep history in order.
    const modified = new Date(Math.max((item.modified ?? now).getTime(), snapshots.length ? snapshots[snapshots.length - 1].date.getTime() : 0));
    const trashed = this.trashUUIDs.has(item.uuid);

    // Only a fragment whose document is missing is made afresh.
    const existingID = this.fragmentByUUID.get(item.uuid);
    const isNew = !existingID || !editor.has(fragmentDocName(existingID));
    // A new item takes the id links were given; one whose document is missing a fresh one.
    const newID = existingID ? undefined : this.linkTargets.get(item.uuid);
    const id = isNew ? editor.at(created, () => editor.createFragment({ id: newID, kind, title: item.title, createdAt: created })) : existingID!;
    const name = fragmentDocName(id);
    this.fragmentByUUID.set(item.uuid, id);
    this.seen.add(id);
    const headsBefore = A.getHeads(editor.document(name));

    // Snapshots become back-dated history entries, oldest first, before the current text (new fragments only).
    if (isNew) {
      for (const snapshot of snapshots) {
        const message = `Scrivener snapshot: ${snapshot.title} (${toISO8601(snapshot.date)})`;
        let markdown: string;
        try { markdown = convertRTF(pkg.files.read(snapshot.path)!).markdown; } catch { this.issue(item.title, `Snapshot '${snapshot.title}' could not be read`); continue; }
        editor.edit(name, message, snapshot.date, (_w, _r, d) => A.updateText(d as A.Doc<unknown>, ['text'], markdown));
        this.report.snapshotsImported += 1;
      }
    }

    // What Scrivener says now. A source that's there but can't be read is null (and reported), and that field isn't
    // merged: the base keeps its previous value, so a later re-import stays three-way.
    let scrivText: string | null = null;
    let scrivRefs: AttachmentRef[] | null = [];
    const rtf = pkg.rtf('content.rtf', item.uuid);
    const media = pkg.mediaFile(item.uuid);
    if (rtf && item.isScript && kind === 'screenplay') {
      // A script: Fountain, read from Scrivener's paragraph formats. Its attachments are left alone.
      scrivRefs = null;
      try {
        scrivText = scrivenerScriptFountain(rtf, scrivenerScriptFormat(pkg));
        this.conversions.set(item.uuid, { markdown: scrivText, sourcePlainText: rtfPlainText(rtf), anchors: [], attachments: [], issues: [] });
      } catch (e) {
        this.issue(item.title, `Script could not be read: ${(e as Error).message}`);
      }
    } else if (rtf) {
      try {
        const converted = convertRTF(rtf);
        this.conversions.set(item.uuid, converted);
        scrivText = linkedConversion(converted, this.linkTargets).markdown;
        for (const m of converted.issues) this.issue(item.title, m);
        const refs: AttachmentRef[] = [];
        for (const attachment of converted.attachments) refs.push(this.storeAttachment(attachment.data, attachment.filename));
        if (media) refs.push(this.storeAttachment(pkg.files.read(media)!, media.split('/').pop()!));
        scrivRefs = refs;
      } catch (e) {
        this.issue(item.title, `Text could not be read: ${(e as Error).message}`);
        scrivText = null;
        scrivRefs = null;
      }
    } else {
      if (media) scrivRefs = [this.storeAttachment(pkg.files.read(media)!, media.split('/').pop()!)];
      // A document whose text Scrivener has emptied keeps only its snapshots: the document is empty now.
      if (isNew && snapshots.length) scrivText = '';
    }
    const scrivSynopsis = pkg.synopsis(item.uuid)?.trim() ?? null;
    const notesRTF = pkg.rtf('notes.rtf', item.uuid);
    let scrivNotes: string | null = null;
    if (notesRTF) {
      try { scrivNotes = rtfPlainText(notesRTF); } catch (e) { this.issue(item.title, `Notes could not be read: ${(e as Error).message}`); }
    }

    const scrivMetadata: Record<string, MetadataValue> = {};
    if (item.labelID) scrivMetadata.label = { type: 'text', value: labelOption(item.labelID) };
    if (item.statusID) scrivMetadata.status = { type: 'text', value: statusOption(item.statusID) };
    if (item.sectionType) scrivMetadata['section-type'] = { type: 'text', value: sectionOption(item.sectionType) };
    const scrivTags = parseTags(item.keywordIDs.map((k) => pkg.keywordTitles[k]).filter((t): t is string => !!t).join(', '));
    if (scrivTags.length) scrivMetadata[TAGS_FIELD] = { type: 'text', value: joinTags(scrivTags) };
    for (const field of pkg.customFields) {
      const raw = item.customMetadata[field.id];
      if (raw === undefined) continue;
      switch (field.type) {
        case 'Checkbox': scrivMetadata[customField(field.id)] = { type: 'boolean', value: raw === 'Yes' }; break;
        case 'List': scrivMetadata[customField(field.id)] = { type: 'text', value: `${customField(field.id)}-${raw}` }; break;
        case 'Date': {
          const date = parseScrivenerDateLoose(raw);
          scrivMetadata[customField(field.id)] = date ? { type: 'date', value: date } : { type: 'text', value: raw };
          break;
        }
        default: scrivMetadata[customField(field.id)] = { type: 'text', value: raw };
      }
    }

    // What Scrivener said last time. Fragments imported before bases were recorded use their state after the last
    // import commit.
    const doc = editor.document(name);
    const fragment = readFragment(doc);
    let base: ImportBase | null = null;
    if (!isNew) {
      base = readImportBase(doc);
      if (!base) {
        const heads = headsAfterMessage(doc, IMPORT_MESSAGES);
        if (heads) {
          const past = readFragment(doc, heads);
          base = { title: past.title, text: past.text, synopsis: past.synopsis, notes: past.notes, includeInCompile: past.includeInCompile, metadata: { ...past.metadata } };
        }
      }
    }
    const incoming: ImportBase = {
      title: item.title,
      text: scrivText ?? base?.text ?? fragment.text,
      synopsis: scrivSynopsis ?? base?.synopsis ?? fragment.synopsis,
      notes: scrivNotes ?? base?.notes ?? fragment.notes,
      includeInCompile: item.includeInCompile,
      metadata: scrivMetadata,
      trashed,
      ...(scrivRefs ? { attachments: scrivRefs.map((r) => r.sha256) } : base?.attachments ? { attachments: base.attachments } : {}),
      ...(base?.comments ? { comments: base.comments } : {}),
    };

    // Three-way: take Scrivener's value only where the suite hasn't changed it since the last import. Where both
    // changed, the suite's value stays and the conflict is reported.
    const conflicts: string[] = [];
    const merge = <T>(what: string, b: T | undefined, suite: T, scrivener: T, same: (x: T, y: T) => boolean, apply: (v: T) => void) => {
      if (b === undefined) { apply(scrivener); return; }
      if (same(scrivener, b) || same(suite, scrivener)) return;
      if (same(suite, b)) { apply(scrivener); return; }
      conflicts.push(what);
    };
    const eq = <T>(x: T, y: T) => x === y;
    const suiteTags = fragmentTags(fragment);
    const entryTrashed = this.project().fragments.get(id)?.trashed;
    const message = isNew ? 'Imported from Scrivener' : 'Re-imported from Scrivener';
    const changed = editor.edit(name, message, isNew ? modified : now, (w, _r, d) => {
      const dd = d as A.Doc<unknown>;
      w.putIfChanged([], 'kind', str(kind));
      merge('title', base?.title, fragment.title, incoming.title, eq, (v) => w.putIfChanged([], 'title', str(v)));
      if (scrivText !== null) {
        // A base from before links (its links were comments) matches Scrivener's text without them: Scrivener hasn't
        // changed it. Untouched here, the document takes Scrivener's text with its links; changed here, it's kept.
        const old = base?.text;
        if (old !== undefined && old !== incoming.text && removingLinks(incoming.text) === old) {
          if (fragment.text === old) A.updateText(dd, ['text'], incoming.text);
        } else {
          merge('text', old, fragment.text, incoming.text, eq, (v) => A.updateText(dd, ['text'], v));
        }
      }
      if (scrivSynopsis !== null) merge('synopsis', base?.synopsis, fragment.synopsis, incoming.synopsis, eq, (v) => A.updateText(dd, ['synopsis'], v));
      if (scrivNotes !== null) merge('notes', base?.notes, fragment.notes, incoming.notes, eq, (v) => A.updateText(dd, ['notes'], v));
      if (scrivRefs) mergeAttachments(d, scrivRefs, isNew ? [] : base?.attachments);
      merge('include in compile', base?.includeInCompile, fragment.includeInCompile, incoming.includeInCompile, eq, (v) => w.putIfChanged([], 'includeInCompile', bool(v)));
      w.ensure(['metadata']);
      const keys = new Set([...Object.keys(incoming.metadata), ...Object.keys(base?.metadata ?? {})]);
      for (const key of keys) {
        if (key === TAGS_FIELD) continue;
        merge(key, base ? base.metadata[key] ?? null : undefined, fragment.metadata[key] ?? null, incoming.metadata[key] ?? null, sameValue, (v) => {
          if (v) w.put(['metadata'], key, metadataScalar(v));
          else if (fragment.metadata[key]) w.delete(['metadata'], key);
        });
      }
      // Tags merge as a set, so the two apps' tags are never in conflict: what Scrivener added since the last import
      // is added, what it took off is taken off, and the suite's own tags stay.
      if (incoming.metadata[TAGS_FIELD] || base?.metadata[TAGS_FIELD]) {
        const baseValue = base?.metadata[TAGS_FIELD];
        const baseTags = base ? (baseValue?.type === 'text' ? parseTags(baseValue.value) : []) : null;
        let tags = suiteTags;
        for (const tag of scrivTags) if (!hasTag(baseTags ?? [], tag)) tags = addingTag(tag, tags);
        for (const tag of baseTags ?? []) if (!hasTag(scrivTags, tag)) tags = removingTag(tag, tags);
        const joined = joinTags(tags);
        if (joined) w.putIfChanged(['metadata'], TAGS_FIELD, str(joined));
        else if (fragment.metadata[TAGS_FIELD]) w.delete(['metadata'], TAGS_FIELD);
      }
      w.putIfChanged([], IMPORT_BASE_KEY, str(encodeImportBase(incoming)));
      if (isNew) w.putIfChanged([], 'modifiedAt', timestamp(modified));
    });
    if (changed && !isNew) editor.edit(name, message, now, (w) => w.putIfChanged([], 'modifiedAt', timestamp(modified)));
    if (conflicts.includes('text')) {
      editor.at(now, () => editor.addSuggestion(id, {
        range: [0, [...fragment.text].length], proposedText: incoming.text, author: 'Scrivener', authorKind: 'human',
        note: 'Changed in both Scrivener and Five Rooms since the last import', message,
      }));
    }

    // Trash is three-way too: a scene trashed in the suite stays trashed unless Scrivener changed it since. Before
    // bases recorded it, either app having trashed it is enough (trash never deletes).
    let resolvedTrashed = trashed;
    if (!isNew && entryTrashed !== undefined) {
      resolvedTrashed = entryTrashed;
      if (base?.trashed !== undefined) merge('trash', base.trashed, entryTrashed, trashed, eq, (v) => { resolvedTrashed = v; });
      else resolvedTrashed = entryTrashed || trashed;
    }
    if (conflicts.length) {
      this.report.conflicts += 1;
      this.issue(item.title, `Changed in both apps since the last import (${conflicts.join(', ')}); kept Five Rooms' version` + (conflicts.includes('text') ? " and added Scrivener's text as a suggestion" : ''));
    }
    const after = readFragment(editor.document(name));
    if (after.title === incoming.title && after.includeInCompile === incoming.includeInCompile
      && sameValue(after.metadata.label ?? null, incoming.metadata.label ?? null) && (scrivText === null || after.text === scrivText)) {
      this.verifiable.add(item.uuid);
    }
    if (isNew) this.report.fragmentsCreated += 1;
    else if (!sameHeads(A.getHeads(editor.document(name)), headsBefore)) this.report.fragmentsUpdated += 1;
    else this.report.fragmentsUnchanged += 1;

    // The project's entry (`adoptFragment`).
    editor.edit('project', undefined, isNew ? modified : now, (w) => {
      const path = ['fragments', id];
      w.putIfChanged(path, 'kind', str(kind));
      w.putIfChanged(path, 'trashed', bool(resolvedTrashed));
      w.putIfChanged(path, 'source', str(`${SCRIVENER_SOURCE}:${item.uuid}`));
    });
  }

  private storeAttachment(bytes: Uint8Array, filename: string): AttachmentRef {
    const sha256 = sha256Hex(bytes);
    if (!this.attachments.has(sha256)) {
      this.attachments.set(sha256, { sha256, filename, mimeType: mimeType(filename), bytes });
      this.report.attachmentsStored += 1;
    }
    return { sha256, filename, mimeType: mimeType(filename) };
  }

  /** Removed items, comments, arrangements, settings, the checkpoint and the verification. */
  finish(): ScrivenerImportResult {
    const { editor, pkg, now } = this;
    // Fragments whose Scrivener item has gone are trashed, never deleted.
    const project = this.project();
    const gone = [...project.fragments].filter(([id, e]) => e.source?.startsWith(`${SCRIVENER_SOURCE}:`) && !this.seen.has(id) && !e.trashed);
    if (gone.length) {
      editor.edit('project', 'Re-imported from Scrivener', now, (w) => { for (const [id] of gone) w.putIfChanged(['fragments', id], 'trashed', bool(true)); });
      this.report.fragmentsRemoved += gone.length;
    }

    for (const item of this.items) this.importAnchors(item);
    this.buildArrangements();

    const edited = pkg.lastModified;
    const recorded = typeof project.settings.scrivenerLastEdit === 'string' ? new Date(project.settings.scrivenerLastEdit) : null;
    editor.edit('project', this.isReimport ? 'Re-imported from Scrivener' : 'Imported from Scrivener', now, (w) => {
      if (edited) {
        const latest = recorded && !Number.isNaN(recorded.getTime()) && recorded > edited ? recorded : edited;
        w.putIfChanged(['settings'], 'scrivenerLastEdit', str(toISO8601(latest)));
      }
      if (pkg.writingHistory.length) this.report.writingHistory = historySummary(pkg.writingHistory, recordImportedHistory(w, pkg.writingHistory));
    });
    editor.at(now, () => editor.createCheckpointBy(this.isReimport ? 'Re-imported from Scrivener' : 'Imported from Scrivener', this.options.author, 'imported'));

    this.report.totalWords = this.countWords();
    this.verify();
    return { projectID: this.projectID, type: this.type, docs: this.docs, report: this.report, attachments: [...this.attachments.values()] };
  }

  /** `importAnchors`: Scrivener's comments, and the links and highlights that aren't in the text, as margin comments,
   *  three-way against the base's list. */
  private importAnchors(item: BinderItem): void {
    const { editor, pkg, now } = this;
    const id = this.fragmentByUUID.get(item.uuid);
    if (!id) return;
    const name = fragmentDocName(id);
    const fragment = readFragment(editor.document(name));
    const existingIDs = new Set(fragment.comments.map((c) => c.id));
    const original = this.conversions.get(item.uuid);
    // Unreadable text: Scrivener's comments are unknown, so none are removed and the base keeps its list.
    if (!original && pkg.rtf('content.rtf', item.uuid)) return;
    // Links are in the text when the document has Scrivener's text with them; otherwise (changed here since links
    // existed, or one that couldn't go in) a link is a margin comment, as before.
    let converted = original;
    if (original && original.anchors.length) {
      const linked = linkedConversion(original, this.fragmentByUUID);
      converted = fragment.text === linked.markdown ? linked : original;
    }
    const base = readImportBase(editor.document(name));
    const importedBefore = base?.comments ? new Set(base.comments) : null;
    const produced = new Set<string>();
    const imported = new Set<string>();
    const stamp = item.modified ?? now;

    if (converted && converted.anchors.length) {
      const bodies = new Map<string, { author: string; body: string; color: string | undefined }>();
      const xml = pkg.commentsXML(item.uuid);
      if (xml) {
        try {
          for (const c of parseXML(xml).childrenNamed('Comment')) {
            const cid = c.attr('ID')?.toUpperCase();
            if (cid) bodies.set(cid, { author: c.attr('Author') ?? this.options.author, body: rtfPlainText(new TextEncoder().encode(c.text)), color: c.attr('Color') });
          }
        } catch (e) { this.issue(item.title, `Comments could not be read: ${(e as Error).message}`); }
      }
      const titles = new Map(this.items.map((i) => [i.uuid.toUpperCase(), i.title]));
      const scalars = [...converted.markdown];
      // After a text conflict the fragment keeps the suite's text, so Scrivener's offsets don't apply: each anchor is
      // found again by what it quotes.
      const sameText = fragment.text === converted.markdown;
      const occurrences = new Map<string, number>();
      for (const anchor of converted.anchors) {
        const quote = scalars.slice(anchor.range[0], Math.min(anchor.range[1], scalars.length)).join('');
        const stableID = (prefix: string) => {
          const key = `${prefix}|${quote}`;
          const n = (occurrences.get(key) ?? 0) + 1;
          occurrences.set(key, n);
          return `${prefix}-${sha256Hex(key, 8)}-${n}`;
        };
        let commentID: string, body: string, author = this.options.author, ink: InkColor | null = null;
        switch (anchor.kind.type) {
          case 'scrivenerComment': {
            commentID = `scrivener-comment-${anchor.kind.uuid.toLowerCase()}`;
            const found = bodies.get(anchor.kind.uuid);
            body = found?.body ?? '(Scrivener comment text missing)';
            author = found?.author ?? this.options.author;
            // A comment coloured in Scrivener goes into the category of that colour (made once, named after the ink);
            // the default yellow, the colour of every comment nobody coloured, means none.
            ink = scrivenerCommentInk(found?.color);
            this.report.commentsImported += 1;
            break;
          }
          case 'documentLink': {
            const linked = anchor.kind.uuid;
            commentID = stableID(`scrivener-link-${linked.toLowerCase()}`);
            const target = this.fragmentByUUID.get(linked) ?? [...this.fragmentByUUID].find(([u]) => u.toUpperCase() === linked.toUpperCase())?.[1];
            body = `Link to “${titles.get(linked.toUpperCase()) ?? 'missing document'}”` + (target ? ` (fragment:${target})` : '');
            this.report.linksPreserved += 1;
            break;
          }
          case 'webLink':
            commentID = stableID('scrivener-weblink');
            body = `Link: ${anchor.kind.url}`;
            this.report.linksPreserved += 1;
            break;
          case 'highlight':
            commentID = stableID(`scrivener-highlight-${anchor.kind.color}`);
            body = `Highlighted in Scrivener (${anchor.kind.color})`;
            this.report.highlightsPreserved += 1;
            break;
        }
        produced.add(commentID);
        if (existingIDs.has(commentID) || importedBefore?.has(commentID)) continue;
        let range = anchor.range;
        if (!sameText) {
          const moved = nearest(quote, anchor.range[0], fragment.text);
          if (!moved) {
            this.issue(item.title, `A Scrivener comment or link on “${quote.slice(0, 40)}” wasn't placed: that passage is different in Five Rooms`);
            continue;
          }
          range = moved;
        }
        // The category is looked up (or made) only for a comment that's about to be added, so a re-import that adds
        // none makes none.
        const category = ink ? this.categoryFor(ink, stamp) : undefined;
        editor.at(stamp, () => editor.addMarginComment(id, { range, body, author, authorKind: 'human', id: commentID, message: 'Imported Scrivener comments', category }));
        imported.add(commentID);
      }
    }
    // Comments this importer created earlier whose source has gone (or whose id scheme changed) are removed, so the
    // margin mirrors Scrivener. Replies keep their parent.
    const parents = new Set(fragment.comments.map((c) => c.parentId).filter((p): p is string => !!p));
    for (const comment of fragment.comments) {
      if (comment.id.startsWith('scrivener-') && !produced.has(comment.id) && !parents.has(comment.id)) editor.at(stamp, () => editor.removeComment(id, comment.id));
    }
    if (base) {
      const kept = [...produced].filter((c) => existingIDs.has(c) || importedBefore?.has(c));
      base.comments = [...new Set([...imported, ...kept])].sort();
      editor.edit(name, 'Imported Scrivener comments', stamp, (w) => w.putIfChanged([], IMPORT_BASE_KEY, str(encodeImportBase(base))));
    }
  }

  /** The project's category named after the ink ("Red"), made in that ink the first time; a category the writer has
   *  since renamed isn't found by the ink's name, so later comments in that colour get a fresh "Red". */
  private categoryFor(ink: InkColor, at: Date): string {
    const name = inkCategoryName(ink);
    const existing = categoryNamed(name, readCommentCategories(this.project()));
    if (existing) return existing.id;
    return this.editor.at(at, () => this.editor.addCommentCategory(name, ink)).id;
  }

  /** `buildArrangements`: Manuscript, Research & Notes, Trash and the collections, each three-way by its signature. */
  private buildArrangements(): void {
    const { editor, pkg, now } = this;
    const arrangementID = (name: string, role: Arrangement['role'], fixedID: string): string => {
      const existing = this.project().arrangements.find((a) => a.id === fixedID || (a.role === role && role === 'manuscript'));
      if (existing) return existing.id;
      return editor.at(now, () => editor.createArrangement({ id: fixedID, name, role }));
    };
    const manuscript = arrangementID('Manuscript', 'manuscript', 'manuscript');
    this.replaceNodes(manuscript, this.draft?.children ?? []);
    this.report.arrangements.push(this.project().arrangements.find((a) => a.id === manuscript)?.name ?? 'Manuscript');
    const reference = pkg.binder.filter((b) => b.type !== 'DraftFolder' && b.type !== 'TrashFolder');
    if (reference.length) {
      this.replaceNodes(arrangementID('Research & Notes', 'reference', 'research'), reference);
      this.report.arrangements.push('Research & Notes');
    }
    if (this.trashRoot && this.trashRoot.children.length) {
      this.replaceNodes(arrangementID('Trash', 'trash', 'trash'), this.trashRoot.children);
      this.report.arrangements.push('Trash');
    }
    const all = pkg.allItems();
    for (const collection of pkg.collections) {
      if (!collection.binderUUIDs.length) continue;
      const id = arrangementID(collection.title, 'collection', `collection-${collection.id.toLowerCase()}`);
      editor.edit('project', undefined, now, (w) => w.putIfChanged(['arrangements', id], 'name', str(collection.title)));
      const flat = collection.binderUUIDs.flatMap((uuid) => { const item = all.find((i) => i.uuid === uuid); return item ? [{ ...item, children: [] }] : []; });
      this.replaceNodes(id, flat);
      this.report.arrangements.push(collection.title);
    }
  }

  /** `replaceNodes`: brings an arrangement in line with Scrivener's binder, three-way. Node ids derive from Scrivener
   *  UUIDs, so an unchanged binder produces no operations. Only the importer's own nodes are compared (in reading order,
   *  with their parents): nodes the suite created are never removed and don't count as restructuring, and they keep
   *  their places as Scrivener's nodes move around them (one whose parent Scrivener removed takes that parent's place).
   *  Scenes trashed in the suite are left out on every side. If both Scrivener and the suite moved Scrivener's nodes
   *  since the last import, the suite's structure stays and Scrivener's goes into a new alternate arrangement. */
  private replaceNodes(arrangementID: string, items: BinderItem[]): void {
    const { editor, now } = this;
    const project = this.project();
    const arrangement = project.arrangements.find((a) => a.id === arrangementID)!;
    const trashed = arrangement.role === 'trash' ? new Set<string>() : new Set([...project.fragments].filter(([, e]) => e.trashed).map(([id]) => id));
    const all: ArrangementNode[] = [];
    const wanted: ArrangementNode[] = [];
    const walk = (list: BinderItem[], parent: string | undefined) => {
      const positions = sequence(list.length);
      list.forEach((item, k) => {
        const nodeID = scrivenerNodeID(item.uuid);
        const node: ArrangementNode = isRootFolder(item)
          ? { id: nodeID, title: item.title, isFolder: true, parentID: parent, position: positions[k] }
          : { id: nodeID, fragmentID: this.fragmentByUUID.get(item.uuid), isFolder: item.type === 'Folder', parentID: parent, position: positions[k] };
        all.push(node);
        // A trashed scene's children (if any aren't trashed) sit where it was.
        const skipped = node.fragmentID !== undefined && trashed.has(node.fragmentID);
        if (!skipped) wanted.push(node);
        walk(item.children, skipped ? parent : nodeID);
      });
    };
    walk(items, undefined);
    const skippedIDs = new Set(all.map((n) => n.id).filter((id) => !wanted.some((w) => w.id === id)));
    const signature = (lines: string) => lines.split('\n').filter((l) => l && !skippedIDs.has(l.slice(0, l.indexOf('|')))).join('\n');
    const scrivenerSignature = all.map((n) => `${n.id}|${n.parentID ?? ''}`).join('\n');
    const wantedSignature = signature(scrivenerSignature);
    const currentSignature = signature(importedSignature(arrangement));
    const bases = project.settings.importBases;
    const storedBase = bases && typeof bases === 'object' ? (bases as Record<string, unknown>)[arrangementID] : undefined;
    const baseSignature = typeof storedBase === 'string' ? signature(storedBase) : currentSignature;
    const recordBase = (w: Writer) => {
      w.ensure(['settings', 'importBases']);
      w.putIfChanged(['settings', 'importBases'], arrangementID, str(scrivenerSignature));
    };

    if (wantedSignature === baseSignature || wantedSignature === currentSignature) {
      // Scrivener's structure hasn't changed (the suite's edits stand), or they already agree. Still refresh folder
      // titles and fragment links.
      editor.edit('project', undefined, now, (w) => {
        for (const node of wanted) {
          const existing = arrangement.nodes.get(node.id);
          if (existing) putNode(w, arrangementID, { ...existing, fragmentID: node.fragmentID, title: node.title, isFolder: node.isFolder });
        }
        recordBase(w);
      });
      return;
    }
    if (currentSignature !== baseSignature) {
      // Both sides restructured: keep the suite's, and put Scrivener's alongside.
      const name = `${arrangement.name} (Scrivener, ${shortDate(now)})`;
      editor.at(now, () => editor.createArrangement({ name, role: 'alternate', nodes: wanted }));
      editor.edit('project', undefined, now, (w) => recordBase(w));
      this.report.structureConflicts.push(arrangement.name);
      this.report.arrangements.push(name);
      return;
    }
    // Only Scrivener restructured: its nodes move, and the suite's keep their places among them.
    const wantedIDs = new Set(wanted.map((n) => n.id));
    const doomed = new Set([...arrangement.nodes.keys()].filter((id) => id.startsWith('scrivener-') && !wantedIDs.has(id)));
    const placed = placeSuiteNodes(arrangement, wanted, doomed);
    editor.edit('project', undefined, now, (w) => {
      for (const node of wanted) putNode(w, arrangementID, node);
      for (const node of placed) putNode(w, arrangementID, node);
      // Removing a node removes its subtree: by now only doomed nodes are left under these.
      for (const id of doomed) w.delete(['arrangements', arrangementID, 'nodes'], id);
      recordBase(w);
    });
  }

  private countWords(): number {
    const project = this.project();
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    if (!manuscript) return 0;
    let total = 0;
    const walk = (nodes: ArrangementTreeNode[]) => {
      for (const n of nodes) {
        const fid = n.node.fragmentID;
        if (fid && this.editor.has(fragmentDocName(fid))) {
          const f = readFragment(this.editor.document(fragmentDocName(fid)));
          total += wordCount(f.text, f.kind);
        }
        walk(n.children);
      }
    };
    walk(tree(manuscript));
    return total;
  }

  /** `ImportVerifier.verify`: every word arrived (of the items that took Scrivener's values), and the manuscript's
   *  structure is Scrivener's (unless both apps restructured it). */
  private verify(): void {
    const v = this.report.verification;
    const only = this.isReimport ? this.verifiable : null;
    const format = scrivenerScriptFormat(this.pkg);
    const play = isStageFormat(format) || format === 'radio';
    for (const item of this.items) {
      const id = this.fragmentByUUID.get(item.uuid);
      if (!id) { v.structureMismatches.push(`“${item.title}” was not imported`); continue; }
      const converted = this.conversions.get(item.uuid);
      if (!converted || (only && !only.has(item.uuid))) continue;
      v.itemsChecked += 1;
      const fragment = readFragment(this.editor.document(fragmentDocName(id)));
      const imported = fragment.kind === 'screenplay' ? fountainPlainText(fragment.text) : plainText(fragment.text);
      let a = normalisedWords(converted.sourcePlainText), b = normalisedWords(imported);
      if (play && item.isScript && fragment.kind === 'screenplay') {
        // A play's act and scene headings are the binder's, its directions lose their brackets, its names their colons
        // and case, a set description gains "Setting:", and a speech a direction broke into goes on under its name
        // again: the names are left out of the comparison.
        const names = new Set(fountainLines(fragment.text).filter((l) => l.element === 'character')
          .flatMap((l) => playWords(l.inlines.map((i) => ('text' in i ? i.text : '')).join(''), false)));
        a = playWords(converted.sourcePlainText, true).filter((w) => !names.has(w));
        b = playWords(imported, false).filter((w) => !names.has(w));
      }
      if (a.join(' ') !== b.join(' ')) v.textMismatches.push(`“${item.title}”: ${a.length} words in Scrivener, ${b.length} imported`);
    }
    const project = this.project();
    const manuscript = project.arrangements.find((a) => a.role === 'manuscript');
    if (this.draft && manuscript && !this.report.structureConflicts.includes(manuscript.name)) {
      const trashed = new Set([...project.fragments].filter(([, e]) => e.trashed).map(([id]) => id));
      const compare = (items: BinderItem[], nodes: ArrangementTreeNode[], path: string) => {
        const ours = nodes.filter((n) => n.node.id.startsWith('scrivener-'));
        const expected = items.filter((i) => { const fid = this.fragmentByUUID.get(i.uuid); return !(fid && trashed.has(fid)); });
        if (ours.length !== expected.length) { v.structureMismatches.push(`${path}: ${expected.length} items in Scrivener, ${ours.length} imported`); return; }
        expected.forEach((item, k) => {
          const node = ours[k];
          if (node.node.id !== scrivenerNodeID(item.uuid)) { v.structureMismatches.push(`${path} › ${item.title}: a different item is here`); return; }
          compare(item.children, node.children, `${path} › ${item.title}`);
        });
      };
      compare(this.draft.children, tree(manuscript), 'Manuscript');
    }
  }
}

/** Imports whole, synchronously (inside one edit of an open project). */
export function importScrivenerPackageSync(pkg: ScrivenerPackage, options: ScrivenerImportOptions): ScrivenerImportResult {
  const run = new ScrivenerImport(pkg, options);
  run.prepare();
  for (let i = 0; i < run.items.length; i++) {
    options.progress?.(i, run.items.length, run.items[i].title);
    run.importItem(i);
  }
  const result = run.finish();
  options.progress?.(run.items.length, run.items.length, '');
  return result;
}

/** Imports the package, yielding between items when `yield` is given so a progress line can show. */
export async function importScrivenerPackage(pkg: ScrivenerPackage, options: ScrivenerImportOptions): Promise<ScrivenerImportResult> {
  const run = new ScrivenerImport(pkg, options);
  run.prepare();
  for (let i = 0; i < run.items.length; i++) {
    options.progress?.(i, run.items.length, run.items[i].title);
    if (options.yield) await options.yield();
    run.importItem(i);
  }
  const result = run.finish();
  options.progress?.(run.items.length, run.items.length, '');
  return result;
}

// MARK: Helpers

/** `ScrivenerImporter.linked`: the conversion with its web links, and links to documents in the project (`fragment:`),
 *  in the text; the anchors left (comments, highlights, links that couldn't go in) moved to where their words are now. */
export function linkedConversion(conversion: RTFConversion, targets: Map<string, string>): RTFConversion {
  const indices: number[] = [];
  const candidates: { range: [number, number]; address: string }[] = [];
  conversion.anchors.forEach((anchor, i) => {
    let address: string | null = null;
    if (anchor.kind.type === 'webLink') address = linkAddress(anchor.kind.url);
    else if (anchor.kind.type === 'documentLink') {
      const uuid = anchor.kind.uuid;
      const target = targets.get(uuid) ?? [...targets].find(([key]) => key.toUpperCase() === uuid)?.[1];
      address = target !== undefined ? `fragment:${target}` : null;
    }
    if (address === null || !isLinkAddress(address)) return;
    indices.push(i);
    candidates.push({ range: [anchor.range[0], anchor.range[1]], address });
  });
  if (!candidates.length) return conversion;
  const result = linking(conversion.markdown, candidates);
  const inText = new Set(indices.filter((_, k) => result.applied[k]));
  return {
    ...conversion,
    markdown: result.text,
    anchors: conversion.anchors.filter((_, i) => !inText.has(i)).map((anchor) => ({ kind: anchor.kind, range: result.range(anchor.range) })),
  };
}

/** `ScrivenerImporter.importedSignature`: the importer's nodes in reading order with their parents. */
export function importedSignature(arrangement: Arrangement): string {
  const lines: string[] = [];
  const walk = (nodes: ArrangementTreeNode[]) => {
    for (const n of nodes) {
      if (n.node.id.startsWith('scrivener-')) lines.push(`${n.node.id}|${n.node.parentID ?? ''}`);
      walk(n.children);
    }
  };
  walk(tree(arrangement));
  return lines.join('\n');
}

/** `placeSuiteNodes`: after Scrivener's nodes have moved, each suite node goes back after the Scrivener node it followed
 *  (under the same parent; one whose parent Scrivener removed takes that parent's place), so a scene added in the suite
 *  stays where the writer put it however Scrivener renumbers its own. Returns the suite nodes to put with their new
 *  places; the wanted nodes' positions are renumbered in place where a suite node joins them. */
function placeSuiteNodes(old: Arrangement, wanted: ArrangementNode[], doomed: Set<string>): ArrangementNode[] {
  const children = new Map<string | undefined, string[]>();
  const parentOf = new Map<string, string | undefined>();
  const suiteNodes: string[] = [];
  const collect = (nodes: ArrangementTreeNode[], parent: string | undefined) => {
    children.set(parent, nodes.map((n) => n.node.id));
    for (const n of nodes) {
      parentOf.set(n.node.id, parent);
      if (!n.node.id.startsWith('scrivener-')) suiteNodes.push(n.node.id);
      collect(n.children, n.node.id);
    }
  };
  collect(tree(old), undefined);
  if (!suiteNodes.length) return [];
  const wantedByID = new Map(wanted.map((n) => [n.id, n]));
  // Each suite node: the parent it ends up under and the wanted node it follows (null = first).
  const placements: { node: ArrangementNode; parent: string | undefined; after: string | null }[] = [];
  for (const id of suiteNodes) {
    const node = old.nodes.get(id);
    if (!node) continue;
    let reference = id;
    let parent = parentOf.get(id);
    while (parent !== undefined && doomed.has(parent)) { reference = parent; parent = parentOf.get(parent); }
    const siblings = children.get(parent) ?? [];
    let after: string | null = null;
    for (let k = siblings.indexOf(reference) - 1; k >= 0; k--) {
      if (wantedByID.has(siblings[k])) { after = siblings[k]; break; }
    }
    placements.push({ node, parent, after });
  }
  // New positions under each touched parent: the wanted children in order with the suite nodes slotted after theirs.
  const out: ArrangementNode[] = [];
  const touched = new Set(placements.map((p) => p.parent));
  for (const parent of touched) {
    const assembled: { node: ArrangementNode; suite: boolean }[] = wanted.filter((n) => n.parentID === parent).map((n) => ({ node: n, suite: false }));
    const mine = placements.filter((p) => p.parent === parent);
    for (const p of mine.filter((x) => x.after === null).reverse()) assembled.unshift({ node: p.node, suite: true });
    for (const p of mine.filter((x) => x.after !== null)) {
      const at = assembled.findIndex((e) => e.node.id === p.after);
      assembled.splice(at < 0 ? assembled.length : at + 1, 0, { node: p.node, suite: true });
    }
    const positions = sequence(assembled.length);
    assembled.forEach((e, k) => {
      if (e.suite) out.push({ ...e.node, parentID: parent, position: positions[k] });
      else e.node.position = positions[k];
    });
  }
  return out;
}

/** `mergeAttachments`: Scrivener's new ones are added, and ones this importer took from Scrivener before that have gone
 *  there are removed. Attachments added in the suite are never touched, nor is one the writer removed brought back.
 *  `imported` undefined (a base from before this was kept): only adds. */
function mergeAttachments(d: Record<string, any>, scrivener: AttachmentRef[], imported: string[] | undefined): void {
  if (!d.attachments) d.attachments = [];
  const list = d.attachments as { sha256: unknown }[];
  const current = new Set(list.map((a) => String(a.sha256)));
  const before = new Set(imported ?? []);
  for (const ref of scrivener) {
    if (before.has(ref.sha256) || current.has(ref.sha256)) continue;
    list.push({ sha256: new A.ImmutableString(ref.sha256), filename: new A.ImmutableString(ref.filename), mimeType: new A.ImmutableString(ref.mimeType) } as never);
    current.add(ref.sha256);
  }
  if (imported === undefined) return;
  const now = new Set(scrivener.map((r) => r.sha256));
  for (let i = list.length - 1; i >= 0; i--) {
    const sha = String(list[i].sha256);
    if (before.has(sha) && !now.has(sha)) list.splice(i, 1);
  }
}

/** The heads after the most recent change carrying one of `messages`, or null. */
function headsAfterMessage(doc: A.Doc<unknown>, messages: Set<string>): string[] | null {
  const changes = A.getAllChanges(doc);
  for (let i = changes.length - 1; i >= 0; i--) {
    const c = A.decodeChange(changes[i]);
    if (c.message && messages.has(c.message)) return [c.hash];
  }
  return null;
}

function sameHeads(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const x = [...a].sort(), y = [...b].sort();
  return x.every((h, i) => h === y[i]);
}

/** `ImportVerifier.playWords`: a play's words for comparing, without act and scene headings (in Scrivener's text),
 *  "Setting:" labels, brackets, colons and case. */
export function playWords(text: string, headings: boolean): string[] {
  return text.split('\n')
    .filter((line) => !headings || !isActOrSceneHeading(line.replace(/^[ \t]+|[ \t]+$/g, '')))
    // A radio play's END. is printed by compile, not kept.
    .filter((line) => !headings || !['END.', 'END'].includes(line.replace(/^[ \t]+|[ \t]+$/g, '').toUpperCase()))
    .map((line) => line.replace(/^\s*(Setting|SETTING):\s*/, ''))
    .flatMap((line) => normalisedWords(line))
    .map((w) => w.toLowerCase().replace(/^[():]+|[():]+$/g, ''))
    .filter(Boolean);
}

/** Words compared ignoring scene-break glyphs, tabs and the object-replacement character (`ImportVerifier.normalisedWords`). */
export function normalisedWords(text: string): string[] {
  return text.replaceAll(OBJECT_REPLACEMENT, ' ').split(/\s+/u).filter((w) => w && !isSceneBreakGlyphs(w));
}

const isSceneBreakGlyphs = (w: string) => [...w].length <= 5 && [...w].every((c) => '#*~§•⁂'.includes(c));

/** A custom Date field's value: Scrivener's own format, else ISO. */
function parseScrivenerDateLoose(raw: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2}) ([+-])(\d{2})(\d{2})$/.exec(raw.trim());
  if (m) {
    const sign = m[7] === '-' ? -1 : 1;
    const offset = sign * (Number(m[8]) * 60 + Number(m[9]));
    return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6])) - offset * 60_000);
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}
